"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Loader2, Sparkles, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { prechartingService } from "../../apis/prechartingService";
import {
  isValidIcd10Code,
  normalizeIcd10Code,
} from "../../utils/icd10Utils";

export default function Icd10CodeChangeModal({
  open,
  onOpenChange,
  currentCode = "",
  currentDescription = "",
  assessmentText = "",
  isReadOnly = false,
  onReplace,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedCodes, setSuggestedCodes] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const searchTimeoutRef = useRef(null);

  const normalizedCurrent = normalizeIcd10Code(currentCode);

  const loadClosestSuggestions = useCallback(async () => {
    const descriptionQuery =
      currentDescription?.trim() || assessmentText?.slice(0, 120)?.trim() || "";

    if (!isValidIcd10Code(normalizedCurrent) && descriptionQuery.length < 2) {
      setSuggestedCodes([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const collected = [];
      const seen = new Set(normalizedCurrent ? [normalizedCurrent] : []);

      const addItems = (items) => {
        if (!Array.isArray(items)) return;
        for (const item of items) {
          const code = normalizeIcd10Code(item.code);
          if (!isValidIcd10Code(code) || seen.has(code)) continue;
          seen.add(code);
          collected.push({
            code,
            description: item.description || item.fullDescription || "",
          });
        }
      };

      // 1) Related & more specific codes from the same ICD-10 family (e.g. all
      //    E11.x codes for a current code of E11.9).
      if (isValidIcd10Code(normalizedCurrent)) {
        const relatedResponse =
          await prechartingService.getRelatedICD10Codes(normalizedCurrent, 12);
        if (relatedResponse.success) {
          addItems(relatedResponse.data);
        }
      }

      // 2) Top up with clinically similar codes matched on the diagnosis
      //    description when the family didn't yield enough suggestions.
      if (collected.length < 6 && descriptionQuery.length >= 2) {
        const descResponse = await prechartingService.searchICD10Codes(
          descriptionQuery,
          8,
        );
        if (descResponse.success) {
          addItems(descResponse.data);
        }
      }

      setSuggestedCodes(collected.slice(0, 10));
    } catch (error) {
      console.error("Error loading ICD-10 suggestions:", error);
      setSuggestedCodes([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [currentDescription, assessmentText, normalizedCurrent]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults([]);
      setSuggestedCodes([]);
      return;
    }
    // Start with an empty search box so the doctor can immediately type a new
    // search instead of clearing the pre-filled current code/description.
    setSearchQuery("");
    setSearchResults([]);
    loadClosestSuggestions();
  }, [open, currentCode, currentDescription, loadClosestSuggestions, normalizedCurrent]);

  useEffect(() => {
    if (!open) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await prechartingService.searchICD10Codes(
          searchQuery.trim(),
          12,
        );
        if (response.success && Array.isArray(response.data)) {
          setSearchResults(
            response.data.filter(
              (item) =>
                normalizeIcd10Code(item.code) !== normalizedCurrent,
            ),
          );
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("ICD-10 search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, open, normalizedCurrent]);

  const handleSelect = async (code, description) => {
    const newCode = normalizeIcd10Code(code);
    if (!isValidIcd10Code(newCode)) return;
    const desc = (description || "").trim();
    const sameCode = newCode === normalizedCurrent;
    const sameDesc =
      desc &&
      currentDescription &&
      desc.toLowerCase() === currentDescription.trim().toLowerCase();
    if (sameCode && sameDesc) {
      onOpenChange(false);
      return;
    }
    if (!onReplace || isReadOnly) return;

    setIsSaving(true);
    try {
      const ok = await onReplace(normalizedCurrent, newCode, desc);
      if (ok) onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-4xl w-[calc(100vw-2rem)] max-h-[92vh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0 space-y-1 pb-0 text-left">
          <DialogTitle>Change ICD-10 code</DialogTitle>
          <DialogDescription className="text-left">
            {isReadOnly
              ? "Viewing code details (SOAP note is read-only)."
              : "Search or pick a code — the ICD-10 code and diagnosis heading in Assessment & Plan will update."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 grid min-w-0 shrink-0 grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch">
          <div className="flex min-h-[7.5rem] min-w-0 flex-col rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Current diagnosis
            </p>
            <Badge
              variant="outline"
              className="mb-3 w-fit font-mono text-base px-3 py-1"
            >
              {normalizedCurrent || "—"}
            </Badge>
            {currentDescription ? (
              <p className="min-w-0 flex-1 text-sm leading-relaxed text-slate-700 break-words">
                {currentDescription}
              </p>
            ) : (
              <p className="text-sm text-slate-400 italic">No description</p>
            )}
          </div>

          <div className="flex min-h-[7.5rem] min-w-0 flex-col justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-4">
            <label
              htmlFor="icd10-change-search"
              className="text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Search replacement
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                id="icd10-change-search"
                type="text"
                placeholder="ICD-10 code or condition name…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 pl-10 text-base"
                disabled={isReadOnly}
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-xs text-slate-500">
              Type at least 2 characters to search the ICD-10 database.
            </p>
          </div>
        </div>

        <div className="mt-4 flex min-h-0 flex-1 min-w-0 flex-col overflow-x-hidden">
          <div className="grid min-h-0 flex-1 min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch">
            {(isLoadingSuggestions || suggestedCodes.length > 0) && (
              <div className="flex min-h-0 min-w-0 flex-col">
                <div className="flex shrink-0 items-center gap-2 mb-2 text-sm font-medium text-slate-700">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Related &amp; more specific codes
                </div>
                {isLoadingSuggestions ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading suggestions…
                  </div>
                ) : (
                  <div className="min-h-0 min-w-0 flex-1 space-y-2 overflow-y-auto pr-1">
                    {suggestedCodes.map((item) => (
                      <SuggestionRow
                        key={item.code}
                        item={item}
                        disabled={isReadOnly || isSaving}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="flex min-h-0 min-w-0 flex-col">
                <p className="mb-2 shrink-0 text-sm font-medium text-slate-700">
                  Search results
                </p>
                <div className="min-h-0 min-w-0 flex-1 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
                  {searchResults.map((item) => (
                    <SuggestionRow
                      key={item.code}
                      item={{
                        code: item.code,
                        description: item.description || item.fullDescription,
                      }}
                      disabled={isReadOnly || isSaving}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {!isSearching &&
            searchQuery.trim().length >= 2 &&
            searchResults.length === 0 &&
            !isLoadingSuggestions && (
              <p className="shrink-0 text-sm text-gray-500 text-center py-2">
                No other codes found. Try a different search term.
              </p>
            )}
        </div>

        <div className="mt-4 flex shrink-0 justify-end border-t border-slate-100 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionRow({ item, onSelect, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(item.code, item.description)}
      className="w-full min-w-0 max-w-full flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left hover:bg-blue-50 hover:border-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
    >
      <Badge variant="outline" className="font-mono shrink-0 mt-0.5">
        {item.code}
      </Badge>
      <span className="min-w-0 flex-1 text-sm text-gray-700 break-words leading-snug">
        {item.description || "—"}
      </span>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
    </button>
  );
}
